if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.setupAddonSizeGate();
      }

      disconnectedCallback() {
        this.addonGateUnsubscribers?.forEach((unsubscribe) => unsubscribe());
      }

      // P1: when the add-on has a data-addon-size (set from the custom.addon_size
      // metafield), only show it while a variant option matching that value is
      // selected, and uncheck it otherwise so it can't be added with other sizes.
      setupAddonSizeGate() {
        this.addonContainer = this.querySelector('.product-addon');
        const gateSize = this.addonContainer?.dataset.addonSize;
        if (!this.addonContainer || !gateSize) return;

        this.addonGateSize = gateSize;
        this.addonProductInfo = this.closest('product-info');
        this.updateAddonSizeGate = this.updateAddonSizeGate.bind(this);
        this.addonGateUnsubscribers = [
          subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.updateAddonSizeGate),
          subscribe(PUB_SUB_EVENTS.variantChange, this.updateAddonSizeGate),
        ];
        this.updateAddonSizeGate();
      }

      updateAddonSizeGate() {
        if (!this.addonContainer || !this.addonGateSize) return;

        const root = this.addonProductInfo || document;
        const variantSelects = root.querySelector('variant-selects');
        const selectedValues = [];
        variantSelects
          ?.querySelectorAll('fieldset input[type="radio"]:checked')
          .forEach((input) => selectedValues.push(input.value));
        variantSelects?.querySelectorAll('select').forEach((select) => selectedValues.push(select.value));

        const matches = selectedValues.includes(this.addonGateSize);
        this.addonContainer.classList.toggle('product-addon--hidden', !matches);
        if (!matches) {
          const checkbox = this.addonContainer.querySelector('.product-addon__checkbox');
          if (checkbox) checkbox.checked = false;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Capture main variant ID once — used in both paths and in publish calls
        const mainVariantId = this.variantIdInput.value;

        // Determine whether the add-on checkbox is active
        const addonCheckbox = this.querySelector('.product-addon__checkbox');
        const addonSelect = this.querySelector('.product-addon__select');
        const addonVariantId = addonSelect ? addonSelect.value : this.dataset.addonVariantId;
        const addonActive = addonCheckbox && addonCheckbox.checked && !addonCheckbox.disabled && addonVariantId;

        let fetchOptions;

        if (addonActive) {
          // Multi-item JSON path: POST { items: [...] } to /cart/add.js
          if (this.cart) this.cart.setActiveElement(document.activeElement);
          const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value) || 1;
          const sections = this.cart ? this.cart.getSectionsToRender().map((s) => s.id) : [];
          const payload = {
            items: [
              { id: parseInt(mainVariantId), quantity: quantity },
              { id: parseInt(addonVariantId), quantity: 1 },
            ],
          };
          if (sections.length) {
            payload.sections = sections;
            payload.sections_url = window.location.pathname;
          }
          fetchOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          };
        } else {
          // Standard FormData path (unchanged behaviour)
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          fetchOptions = config;
        }

        fetch(`${routes.cart_add_url}`, fetchOptions)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: mainVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
